<template>
  <div class="page-content-wrap">
    <FileFullPreviewMode />

    <!-- attributes -->
    <div
      v-for="(attribute, idx) in safeAttributes"
      :key="idx"
      class="row row-service"
    >
      <!-- LABEL COLUMN -->
      <div
        v-if="shouldShowLabel(attribute)"
        class="col-xs-12 col-md-4 col-name"
      >
        {{ labelText(attribute) }}
      </div>

      <!-- VALUE COLUMN -->
      <div
        v-if="shouldShowValue(attribute)"
        class="col-xs-12 col-md-8 col-value"
      >
        <!-- tender purpose -->
        <template v-if="attribute.type === 'tender_purpose'">
          <div class="info-value" v-html="lang(tender.purpose_content)"></div>
        </template>

        <!-- tender description -->
        <template v-else-if="attribute.type === 'tender_description'">
          <div class="tender-description" v-html="lang(tender.description)" />
        </template>

        <!-- text / textarea -->
        <template v-else-if="isTextual(attribute)">
          {{ attribute.valueLang[$app.lang] }}
        </template>

        <!-- wysiwyg -->
        <div
          v-else-if="attribute.type === 'wysiwyg'"
          v-html="attribute.valueLang[$app.lang]"
        />

        <!-- number -->
        <template v-else-if="attribute.type === 'number'">
          {{ attribute.valueNumeric }}
        </template>

        <!-- file / image -->
        <template
          v-else-if="attribute.type === 'file' || attribute.type === 'image'"
        >
          <FilePreview
            :folder="attribute.unique_folder_id"
            :show-file-name="attribute.show_file_name"
            :show-file-size="attribute.show_file_size"
            :show-file-extension="attribute.show_file_extension"
          />
        </template>

        <!-- url list -->
        <div v-else-if="attribute.type === 'url'" class="url-list">
          <div
            v-for="(option, i) in attribute.options"
            :key="i"
            class="url-block"
          >
            <a
              :href="option.content"
              target="_blank"
              rel="noopener"
              class="url-block-link"
            >
              <span class="url-block-icon" aria-hidden="true">
                <i class="fas fa-link" />
              </span>
              <span class="url-block-label">{{ option.text }}</span>
            </a>
          </div>
        </div>

        <!-- service types -->
        <div
          v-else-if="attribute.type === 'tender_service_types'"
          class="tc-feats"
        >
          <span
            v-for="(s, sIdx) in tender.tender_service_types || []"
            :key="`s-${sIdx}`"
            class="tc-feat tc-feat-s"
            :title="s"
          >
            {{ s }}
          </span>
        </div>

        <!-- assortments -->
        <div
          v-else-if="attribute.type === 'tender_assortments'"
          class="tc-feats"
        >
          <span
            v-for="(a, aIdx) in tender.tender_assortments || []"
            :key="`a-${aIdx}`"
            class="tc-feat tc-feat-a"
            :title="a"
          >
            {{ a }}
          </span>
        </div>

        <!-- participants -->
        <template v-else-if="attribute.type === 'tender_participants'">
          <template
            v-if="
              isOne(tender.dedicated_individual) &&
              isOne(tender.dedicated_legal)
            "
          >
            Fizinis arba juridinis asmuo
          </template>
          <template
            v-else-if="
              isOne(tender.dedicated_individual) &&
              !isOne(tender.dedicated_legal)
            "
          >
            Fizinis asmuo
          </template>
          <template
            v-else-if="
              !isOne(tender.dedicated_individual) &&
              isOne(tender.dedicated_legal)
            "
          >
            Juridinis asmuo
          </template>
        </template>

        <!-- departments -->
        <div v-else-if="attribute.type === 'tender_department'">
          <template v-if="tender.tender_departments">
            <div
              v-for="(dep, dix) in tender.tender_departments"
              :key="dix"
              class="contact-row"
            >
              <template v-if="dep.website">
                <a
                  class="service-link"
                  :href="dep.website"
                  target="_blank"
                  rel="noopener"
                >
                  {{ dep.name }}
                </a>
              </template>
              <template v-else>
                {{ dep.name }}
              </template>
            </div>
          </template>
        </div>

        <!-- contacts -->
        <div v-else-if="attribute.type === 'tender_contacts'">
          <template v-if="tender.tender_contacts">
            <div
              v-for="(contact, cix) in tender.tender_contacts"
              :key="cix"
              class="contact-row"
            >
              <template v-if="contact.email">
                <a :href="`mailto:${contact.email}`" class="service-link">
                  {{ contact.first_name }} {{ contact.last_name }}
                </a>
              </template>
              <template v-else>
                {{ contact.first_name }} {{ contact.last_name }}
              </template>

              <template v-if="contact.phone">
                ,&nbsp;
                <a
                  :href="`tel:${contact.phone}`"
                  class="service-link service-link-phone"
                >
                  {{ contact.phone }}
                </a>
              </template>
            </div>
          </template>
        </div>

        <!-- updated at -->
        <template v-else-if="attribute.type === 'tender_updated_at'">
          {{ tender.tender_updated_at }}
        </template>

        <!-- location name -->
        <template v-else-if="attribute.type === 'location_name'">
          {{ tender.location_name }}
        </template>

        <!-- location number by scheme -->
        <template v-else-if="attribute.type === 'location_number_by_scheme'">
          {{ tender.location_number_by_scheme }}
        </template>

        <!-- tender status -->
        <div
          v-else-if="attribute.type === 'tender_status'"
          class="tp-statusbar"
          role="status"
          :aria-label="statusAria"
        >
          <div
            v-if="badgeText"
            class="tc-badge"
            :class="`tc-badge--${tenderStatus}`"
          >
            {{ badgeText }}
          </div>

          <div
            v-if="countdownText"
            class="tc-timer"
            :class="`tc-timer--${tenderStatus}`"
            v-html="countdownText"
          />
        </div>

        <!-- tender form -->
        <div v-else-if="attribute.type === 'tender_form'">
          <!-- Show submit CTA only during accepting window and when not archived -->
          <template v-if="canSubmitTender">
            <a
              v-if="$app.isLoggedIn"
              class="btn btn-primary blue"
              :href="tender.form_url"
            >
              Teikti prašymą
            </a>
            <button
              v-else
              class="btn btn-primary blue"
              type="button"
              @click="modalAuthDialog"
            >
              {{ $t('account.nav.login') }}
            </button>
          </template>

          <!-- Outside accepting window: if user has a record, offer a link to it -->
          <template v-else>
            <a
              v-if="$app.isLoggedIn && userParticipated"
              class="btn btn-secondary blue-light-fill-in"
              :href="myTenderUrl"
            >
              Mano prašymas
            </a>
            <div
              v-if="
                !multipleApplicationsAllowed &&
                $app.isLoggedIn &&
                userParticipated
              "
              class="tender-cta-note text-muted mt-2"
            >
              Jūs jau pateikėte prašymą šiam konkursui.
            </div>
            <div v-else class="tender-cta-note text-muted">
              <template v-if="isArchived">Konkursas archyvuotas</template>
              <template v-else-if="tenderStatus === 'upcoming_open'">
                Prašymų priėmimas dar neprasidėjo.
                <span v-if="tender.submissions_open_date">
                  Pradžia: <strong>{{ tender.submissions_open_date }}</strong>
                </span>
              </template>
              <template
                v-else-if="
                  tenderStatus === 'waiting_envelope' ||
                  tenderStatus === 'envelopes_opened' ||
                  tenderStatus === 'closed'
                "
              >
                Prašymų priėmimas baigtas.
              </template>
              <template v-else>
                Informacija apie prašymų priėmimą netrukus.
              </template>
            </div>
          </template>
        </div>

        <!-- minimum fee -->
        <template v-else-if="attribute.type === 'minimum_fee'">
          <div>
            {{ tender.minimum_fee }} EUR
            <div class="tp-minfee-words text-muted text-left">
              {{ tender.minimum_fee_words }}
            </div>
          </div>
        </template>

        <!-- participation fee -->
        <template v-else-if="attribute.type === 'participation_fee'">
          <div>
            {{ tender.participation_fee }} EUR
            <div class="tp-minfee-words text-muted text-left">
              {{ tender.participation_fee_words }}
            </div>
          </div>
        </template>

        <!-- tender term -->
        <template v-else-if="attribute.type === 'tender_term'">
          <span class="tp-tender-term">{{ tender.tender_term }}</span>
        </template>

        <!-- submissions dates -->
        <template v-else-if="attribute.type === 'submissions_open_date'">
          <strong class="tp-date-open">{{
            tender.submissions_open_date
          }}</strong>
        </template>

        <template v-else-if="attribute.type === 'submissions_close_date'">
          <strong class="tp-date-close">{{
            tender.submissions_close_date
          }}</strong>
        </template>

        <!-- envelope open -->
        <template v-else-if="attribute.type === 'envelope_open_at'">
          {{ tender.envelope_open_at }}
        </template>
      </div>
    </div>

    <!-- Back to Konkursai -->
    <div class="row row-service">
      <div class="col-xs-12 col-md-4 col-name"></div>
      <div class="col-xs-12 col-md-8 col-value">
        <a href="/konkursai" class="btn btn-primary blue-light-fill-in">
          <i class="fas fa-chevron-left"></i> Atgal į konkursus
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import AuthDialog from '@account/js/components/modals/AuthDialog'
import FilePreview from '@assets/js/views/Preview/FilePreview'
import FileFullPreviewMode from '@assets/js/components/Preview/FileFullPreviewMode'

export default {
  name: 'TenderPage',
  components: {
    FilePreview,
    FileFullPreviewMode,
  },
  props: {
    tender: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      nowTick: Date.now(),
      timerId: null,
    }
  },
  computed: {
    // Defensive copy to avoid runtime null/undefined issues in v-for
    safeAttributes() {
      return Array.isArray(this.tender?.data_list) ? this.tender.data_list : []
    },

    // Date checkpoints (local)
    dOpen() {
      return this.parseFlexibleLocal(this.tender?.submissions_open_date, {
        endOfDay: false,
      })
    },
    dClose() {
      return this.parseFlexibleLocal(this.tender?.submissions_close_date, {
        endOfDay: true,
      })
    },
    dEnvelope() {
      return this.parseFlexibleLocal(this.tender?.envelope_open_at, {
        endOfDay: false,
      })
    },

    // Status machine
    tenderStatus() {
      const now = new Date(this.nowTick)
      if (this.dOpen && now < this.dOpen) return 'upcoming_open'
      if (this.dOpen && this.dClose && now >= this.dOpen && now <= this.dClose)
        return 'accepting'
      if (this.dClose && now > this.dClose) {
        if (this.dEnvelope && now < this.dEnvelope) return 'waiting_envelope'
        if (this.dEnvelope && now >= this.dEnvelope) return 'envelopes_opened'
        return 'closed'
      }
      return 'unknown'
    },

    badgeText() {
      switch (this.tenderStatus) {
        case 'upcoming_open':
          return 'Neprasidėjo'
        case 'accepting':
          return 'Vyksta prašymų priėmimas'
        case 'waiting_envelope':
          return 'Laukiama vokų atplėšimo'
        case 'envelopes_opened':
          return 'Vokai atplėšti'
        case 'closed':
          return 'Uždaryta'
        default:
          return ''
      }
    },

    countdownText() {
      const now = new Date(this.nowTick)
      if (this.tenderStatus === 'upcoming_open' && this.dOpen) {
        return `Iki prašymų priėmimo pradžios liko ${this.diffHuman(now, this.dOpen)}`
      }
      if (this.tenderStatus === 'accepting' && this.dClose) {
        return `Liko iki prašymų priėmimo pabaigos ${this.diffHuman(now, this.dClose)}`
      }
      if (this.tenderStatus === 'waiting_envelope' && this.dEnvelope) {
        return `Iki vokų atplėšimo liko ${this.diffHuman(now, this.dEnvelope)}`
      }
      return ''
    },

    statusAria() {
      // compact status for screen readers
      const parts = []
      if (this.badgeText) parts.push(this.badgeText)
      if (this.countdownText) {
        // strip tags from diffHuman result
        parts.push(this.countdownText.replace(/<[^>]+>/g, ' '))
      }
      return parts.join('. ')
    },

    // Business flags used by CTA logic
    isArchived() {
      // Treat explicit archived status or disabled state as archived
      const s = this.tender?.status
      const disabled = this.tender?.enabled === false
      return (s && String(s).toLowerCase() === 'archived') || disabled
    },
    multipleApplicationsAllowed() {
      const v = this.tender?.allow_multiple_applications
      if (typeof v === 'boolean') return v
      if (v === 1 || v === '1') return true
      if (v === 0 || v === '0') return false
      // Default: allow multiple when setting is missing
      return true
    },
    canSubmitTender() {
      // Only during accepting window and when not archived
      // When multiple applications are disabled, disallow if user already has a record
      if (this.tenderStatus !== 'accepting' || this.isArchived) return false
      if (!this.multipleApplicationsAllowed && this.userParticipated)
        return false
      return true
    },
    userParticipated() {
      // Heuristics: backend may expose various flags/ids when user has a record
      return !!(
        this.tender?.user_participated ||
        this.tender?.my_form_id ||
        this.tender?.my_record_id ||
        this.tender?.user_form_id
      )
    },
    myTenderUrl() {
      // Prefer direct URL if present; otherwise attempt to build from a known id; fallback to dashboard
      if (this.tender?.my_form_url) return this.tender.my_form_url
      const id =
        this.tender?.my_record_id ||
        this.tender?.my_form_id ||
        this.tender?.user_form_id
      if (id) return `/account/tenders/${id}/submit-review`
      return '/account/tenders'
    },
  },

  mounted() {
    // tick every minute to refresh countdowns
    this.timerId = window.setInterval(() => {
      this.nowTick = Date.now()
    }, 60000)
  },

  beforeDestroy() {
    if (this.timerId) {
      window.clearInterval(this.timerId)
      this.timerId = null
    }
  },

  methods: {
    // Safely resolve localized field values
    lang(obj, fallback = '') {
      if (!obj || typeof obj !== 'object') return fallback
      const cur = this.$app?.lang || 'en'
      return obj[cur] ?? obj.en ?? obj.lt ?? fallback
    },
    /* ---------- UI actions ---------- */
    modalAuthDialog() {
      this.$modal.show(
        AuthDialog,
        { id: this.tender?.id, modal: true },
        {
          name: 'auth-modal',
          clickToClose: false,
          resizable: false,
          adaptive: true,
          draggable: false,
          scrollable: true,
          height: 'auto',
          maxWidth: 780,
          width: '100%',
          transition: 'nice-modal-fade',
        },
      )
    },

    /* ---------- Label / Value visibility ---------- */
    shouldShowLabel(attr) {
      const t = attr?.type
      if (!t) return false

      // special: tender purpose label comes from tender.purpose_label
      if (t === 'tender_purpose') return !!this.lang(this.tender?.purpose_label)

      // special: tender description uses attribute.name
      if (t === 'tender_description')
        return (
          !!this.getAttributeLangValue(attr, 'name', this.$app.lang) &&
          !!this.lang(this.tender?.description)
        )

      // types with localized label text
      if (
        this.isTextual(attr) ||
        t === 'wysiwyg' ||
        t === 'number' ||
        t === 'url'
      ) {
        return (
          this.hasLangValue(attr) ||
          (t === 'number' && this.hasNumberValue(attr)) ||
          (t === 'url' && this.hasUrlOptions(attr))
        )
      }

      // types that display a generic/localized label independent of inner value renderer
      if (t === 'file' || t === 'image') return this.hasFiles(attr)
      if (t === 'tender_service_types')
        return !!this.tender?.tender_service_types?.length
      if (t === 'tender_assortments')
        return !!this.tender?.tender_assortments?.length
      if (t === 'tender_participants')
        return (
          this.isOne(this.tender?.dedicated_individual) ||
          this.isOne(this.tender?.dedicated_legal)
        )
      if (t === 'tender_department')
        return !!this.tender?.tender_departments?.length
      if (t === 'tender_contacts') return !!this.tender?.tender_contacts?.length
      if (t === 'tender_updated_at') return !!this.tender?.tender_updated_at
      if (t === 'location_name') return !!this.tender?.location_name
      if (t === 'tender_status') return true
      if (t === 'tender_form') return true
      if (t === 'location_number_by_scheme')
        return !!this.tender?.location_number_by_scheme
      if (t === 'minimum_fee') return !!this.tender?.minimum_fee
      if (t === 'participation_fee') return !!this.tender?.participation_fee
      if (t === 'tender_term') return !!this.tender?.tender_term
      if (t === 'submissions_open_date')
        return !!this.tender?.submissions_open_date
      if (t === 'submissions_close_date')
        return !!this.tender?.submissions_close_date
      if (t === 'envelope_open_at') return !!this.tender?.envelope_open_at

      return false
    },

    shouldShowValue(attr) {
      const t = attr?.type
      if (!t) return false

      // special: tender purpose/description values come from tender object
      if (t === 'tender_purpose')
        return !!this.lang(this.tender?.purpose_content)
      if (t === 'tender_description')
        return !!this.lang(this.tender?.description)

      if (this.isTextual(attr)) return this.hasLangValue(attr)
      if (t === 'wysiwyg') return this.hasLangValue(attr)
      if (t === 'number') return this.hasNumberValue(attr)
      if (t === 'file' || t === 'image') return this.hasFiles(attr)
      if (t === 'url') return this.hasUrlOptions(attr)

      if (t === 'tender_service_types')
        return !!this.tender?.tender_service_types?.length
      if (t === 'tender_assortments')
        return !!this.tender?.tender_assortments?.length
      if (t === 'tender_participants')
        return (
          this.isOne(this.tender?.dedicated_individual) ||
          this.isOne(this.tender?.dedicated_legal)
        )
      if (t === 'tender_department') return true
      if (t === 'tender_contacts') return true
      if (t === 'tender_updated_at') return !!this.tender?.tender_updated_at
      if (t === 'location_name') return !!this.tender?.location_name
      if (t === 'location_number_by_scheme')
        return !!this.tender?.location_number_by_scheme
      if (t === 'tender_status') return true
      if (t === 'tender_form') return true
      if (t === 'minimum_fee') return !!this.tender?.minimum_fee
      if (t === 'participation_fee') return !!this.tender?.participation_fee
      if (t === 'tender_term') return !!this.tender?.tender_term
      if (t === 'submissions_open_date')
        return !!this.tender?.submissions_open_date
      if (t === 'submissions_close_date')
        return !!this.tender?.submissions_close_date
      if (t === 'envelope_open_at') return !!this.tender?.envelope_open_at

      return false
    },

    labelText(attr) {
      const t = attr?.type
      if (!t) return ''

      // translatable labels from attribute
      if (
        this.isTextual(attr) ||
        t === 'wysiwyg' ||
        t === 'number' ||
        t === 'url'
      ) {
        return (attr?.name && attr.name[this.$app.lang]) || ''
      }

      // file/image may carry translated name too
      if (t === 'file' || t === 'image') {
        return this.getAttributeLangValue(attr, 'name', this.$app.lang)
      }

      // special: tender purpose label from tender.purpose_label
      if (t === 'tender_purpose')
        return this.lang(this.tender?.purpose_label) || ''

      // special: tender description uses attribute.name like other attributes
      if (t === 'tender_description')
        return this.getAttributeLangValue(attr, 'name', this.$app.lang) || ''

      // static labels
      if (t === 'tender_service_types') return 'Paslaugų rūšys'
      if (t === 'tender_assortments') return 'Prekių asortimentas'
      if (t === 'tender_participants') return 'Konkurso dalyviai'
      if (t === 'tender_department') return 'Atsakingas padalinys'
      if (t === 'tender_contacts') return 'Paslaugos vykdytojas'
      if (t === 'tender_updated_at') return 'Informacija atnaujinta'
      if (t === 'location_name') return 'Vieta'
      if (t === 'tender_status') return 'Būsena'
      if (t === 'tender_form') return '' // intentionally blank (button only)
      if (t === 'location_number_by_scheme') return 'Numeris pagal schemą'
      if (t === 'minimum_fee') return 'Nustatyta vietinė rinkliava'
      if (t === 'participation_fee') return 'Dalyvio mokestis'
      if (t === 'tender_term') return 'Terminas kuriam organizuojamas konkursas'
      if (t === 'submissions_open_date') return 'Prašymų teikimo pradžia'
      if (t === 'submissions_close_date') return 'Prašymų teikimo pabaiga'
      if (t === 'envelope_open_at') return 'Vokų plėšimas'

      return ''
    },

    /* ---------- Small helpers ---------- */
    isTextual(attr) {
      return attr?.type === 'text' || attr?.type === 'textarea'
    },
    hasLangValue(attr) {
      return !!attr?.valueLang?.[this.$app.lang]
    },
    hasNumberValue(attr) {
      return typeof attr?.valueNumeric === 'number' || attr?.valueNumeric === 0
    },
    hasFiles(attr) {
      return !!(attr && attr.files && Object.keys(attr.files).length)
    },
    hasUrlOptions(attr) {
      return !!(
        attr &&
        attr.options &&
        typeof attr.options === 'object' &&
        Object.keys(attr.options).length
      )
    },

    // Treat 1, '1', true (and numeric-string 1) as enabled
    isOne(val) {
      if (val === 1 || val === '1' || val === true) return true
      const n = Number(val)
      return !Number.isNaN(n) && n === 1
    },

    getAttributeLangValue(data, param, lang = null) {
      if (!data || !param) return ''
      if (!lang && Object.prototype.hasOwnProperty.call(data, param)) {
        return data[param]
      }
      if (lang && Object.prototype.hasOwnProperty.call(data, param)) {
        const value = data[param]
        if (value && Object.prototype.hasOwnProperty.call(value, lang)) {
          return value[lang]
        }
        return value
      }
      return ''
    },

    parseFlexibleLocal(input, { endOfDay = false } = {}) {
      if (!input) return null
      const str = String(input).trim()
      const hasTime = /\d{2}:\d{2}/.test(str)
      const [datePart, timePart] = str.split(' ')
      const [y, m, d] = (datePart || '').split('-').map(Number)
      if (!y || !m || !d) return null

      if (hasTime) {
        const [hh, mm] = (timePart || '').split(':').map(Number)
        return new Date(y, m - 1, d, hh || 0, mm || 0, 0, 0)
      }
      return endOfDay
        ? new Date(y, m - 1, d, 23, 59, 59, 999)
        : new Date(y, m - 1, d, 0, 0, 0, 0)
    },

    diffHuman(from, to) {
      let ms = Math.max(0, to - from)
      const totalMinutes = Math.floor(ms / 60000)
      const days = Math.floor(totalMinutes / (60 * 24))
      const hours = Math.floor((totalMinutes - days * 24 * 60) / 60)
      const minutes = totalMinutes % 60

      if (days > 0) {
        return `<br><span class="time-part time-d">${days} d.</span> <span class="time-part time-h">${hours} val.</span>`
      }
      if (hours > 0) {
        return `<br><span class="time-part time-h">${hours} val.</span> <span class="time-part time-m">${minutes} min.</span>`
      }
      return `<br><span class="time-part time-m">${minutes} min.</span>`
    },
  },
}
</script>

<style scoped>
.tp-date-open {
  color: #6da4cb;
}
.tp-date-close {
  color: #ed6b5b;
}
.url-list .url-block + .url-block {
  margin-top: 0.25rem;
}
.url-block-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
}
.url-block-link:hover {
  text-decoration: underline;
}
</style>
